import { checkoutCopyFor } from "@/utils/checkoutCopy";
import { useMutation } from "@apollo/client/react";
import ModalProvider from "@repo/components/src/globalProviders/modal";
import { BasicModal } from "@repo/components/src/shared/modal";
import { Badge, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { Card } from "@repo/components/src/ui/card";
import { Divider } from "@repo/components/src/ui/divider";
import {
  FormControl,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Input, InputField } from "@repo/components/src/ui/input";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Spinner } from "@repo/components/src/ui/spinner";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatMoneyFromFragment } from "@repo/components/src/utils/money";
import { formatPercentage } from "@repo/components/src/utils/numbers";
import { CreateSubscriptionCheckoutDocument } from "@repo/graphql/documents/subscription/mutation.generated";
import { PlanPricingFragmentFragment } from "@repo/graphql/documents/workspace/fragment.generated";
import { WorkspacePlanSettingsQuery } from "@repo/graphql/documents/workspace/query.generated";
import { ExternalPathString, router } from "expo-router";
import {
  ArrowUpRight,
  Calendar,
  Check,
  CircleCheckBig,
} from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";

type PriceItem = PlanPricingFragmentFragment["priceSet"][number];

type Props = {
  // Named rather than positional: this used to be a two-element array read as
  // prices[0]/prices[1], which silently depended on the caller's ordering.
  monthlyPrice: PriceItem;
  yearlyPrice: PriceItem;
  plan: NonNullable<
    WorkspacePlanSettingsQuery["subscription"]["listPlans"]
  >[number];
  onSuccess?: () => void;
  /** In-app path to land back on once paid; the dashboard when omitted. */
  returnPath?: string;
};

// Same rule the onboarding modal validates with, kept here because this modal
// only sends the email — a malformed one otherwise fails at the provider.
const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const PricingCard = ({
  price,
  label,
  sublabel,
  isSelected,
  onSelect,
  badge,
}: {
  price: PriceItem;
  label: string;
  sublabel: string;
  isSelected: boolean;
  onSelect: () => void;
  badge?: string;
}) => (
  <Pressable onPress={onSelect} className="flex-1">
    <Card
      variant={isSelected ? "outline" : "ghost"}
      size="md"
      className={`relative ${
        isSelected
          ? "border-brand-500 bg-brand-50"
          : "border-outline-200 bg-background-50"
      }`}
    >
      {badge && (
        <Box className="absolute -top-2.5 right-3">
          <Badge action="success" size="sm" rounded="full">
            <BadgeText className="font-semibold text-2xs">{badge}</BadgeText>
          </Badge>
        </Box>
      )}
      <VStack space="sm">
        <HStack className="items-center justify-between">
          <HStack space="sm" className="items-center">
            <Icon
              as={Calendar}
              size="sm"
              className={isSelected ? "text-brand-600" : "text-typography-400"}
            />
            <Text
              size="sm"
              bold
              className={isSelected ? "text-brand-700" : "text-typography-700"}
            >
              {label}
            </Text>
          </HStack>
          <Icon
            as={isSelected ? CircleCheckBig : Check}
            size="sm"
            className={isSelected ? "text-brand-600" : "text-transparent"}
          />
        </HStack>
        <Divider />
        <VStack space="xs">
          <Heading
            size="xl"
            bold
            className={isSelected ? "text-brand-700" : "text-typography-800"}
          >
            {formatMoneyFromFragment(price.totalValue)}
          </Heading>
          <Text size="xs" action="muted">
            {sublabel}
          </Text>
        </VStack>
      </VStack>
    </Card>
  </Pressable>
);

const CreditCardModal = React.memo((props: Props) => {
  const {
    monthlyPrice,
    yearlyPrice,
    plan: planFragment,
    onSuccess,
    returnPath,
  } = props;
  const [price, setPrice] = useState<PriceItem>(monthlyPrice);
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const plan = planFragment;
  const copy = useMemo(
    () => checkoutCopyFor(price.paymentProviderCode),
    [price],
  );
  const emailError =
    emailTouched && !isValidEmail(email.trim())
      ? "Ingresa un correo válido"
      : null;
  const savingsLabel = useMemo(() => {
    const monthlyAmount = Number(monthlyPrice.totalValue?.amount);
    const yearlyAmount = Number(yearlyPrice.totalValue?.amount);
    if (!monthlyAmount || !yearlyAmount) return undefined;
    const annualizedMonthly = monthlyAmount * 12;
    if (yearlyAmount >= annualizedMonthly) return undefined;
    const savingsRatio = (annualizedMonthly - yearlyAmount) / annualizedMonthly;
    return `Ahorra ${formatPercentage(savingsRatio, 0)}`;
  }, [monthlyPrice, yearlyPrice]);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [createCheckout, { loading, error }] = useMutation(
    CreateSubscriptionCheckoutDocument,
    {
      onCompleted: (data) => {
        const response = data.subscriptions?.createSubscriptionCheckout;
        const userErrors = response?.userErrors ?? [];
        if (userErrors.length > 0) {
          setCheckoutError(
            userErrors[0]?.message ?? "No pudimos generar tu checkout.",
          );
          return;
        }
        const initPoint = response?.initPoint;
        if (initPoint) {
          ModalProvider.hide();
          onSuccess?.();
          router.replace(initPoint as ExternalPathString);
        }
      },
    },
  );
  const handleSubscribe = useCallback(() => {
    setEmailTouched(true);
    if (!isValidEmail(email.trim())) return;
    setCheckoutError(null);
    createCheckout({
      variables: {
        priceId: price.id,
        email: email.trim() || undefined,
        returnPath,
      },
    });
  }, [price, email, returnPath, createCheckout]);

  return (
    <BasicModal title={`Contratar Plan ${plan.name}`}>
      {loading ? (
        <VStack space="md" className="items-center justify-center py-10">
          <Spinner size={"large"} />
          <Text action="muted" size="sm">
            Preparando tu checkout seguro...
          </Text>
        </VStack>
      ) : (
        <VStack space="lg">
          <Text size="sm" action="muted">
            Elige la frecuencia de pago que prefieras. {copy.redirectNotice}
          </Text>

          <FormControl>
            <FormControlLabel>
              <FormControlLabelText>{copy.emailLabel}</FormControlLabelText>
            </FormControlLabel>
            <Input>
              <InputField
                placeholder="tu@email.com"
                value={email}
                onChangeText={setEmail}
                onBlur={() => setEmailTouched(true)}
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </Input>
            {emailError ? (
              <Text action="error" size="xs" className="mt-1">
                {emailError}
              </Text>
            ) : (
              <Text action="muted" size="xs" className="mt-1">
                {copy.emailHelp}
              </Text>
            )}
          </FormControl>

          <HStack space="md">
            <PricingCard
              price={monthlyPrice}
              label="Mensual"
              sublabel="Pago cada mes"
              isSelected={price.id === monthlyPrice.id}
              onSelect={() => setPrice(monthlyPrice)}
            />
            <PricingCard
              price={yearlyPrice}
              label="Anual"
              sublabel="Pago cada año"
              isSelected={price.id === yearlyPrice.id}
              onSelect={() => setPrice(yearlyPrice)}
              badge={savingsLabel}
            />
          </HStack>

          {(checkoutError || error) && (
            <Text action="error" size="sm">
              {checkoutError ?? error?.message}
            </Text>
          )}

          <Button
            action="brand"
            variant="solid"
            rounded="lg"
            size="lg"
            className="w-full"
            onPress={handleSubscribe}
            isDisabled={!isValidEmail(email.trim())}
          >
            <ButtonText>Continuar al pago</ButtonText>
            <ButtonIcon as={ArrowUpRight} />
          </Button>
        </VStack>
      )}
    </BasicModal>
  );
});

CreditCardModal.displayName = "CreditCardModal";

export default CreditCardModal;
